import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Package, Truck, FileText, Calculator, BookOpen,
  Activity, DollarSign, Users, Leaf, Users2, Settings, ChevronsLeft, ChevronsRight,
  Info, Newspaper, ShieldCheck, PenSquare, Link2, Server, FileSpreadsheet, Bookmark, Key, KeyRound,
  Shield, History, FileCheck2, FileCode2, Ship, Tag, Code2, MessageSquare, Mail, Gauge, Home, PlayCircle, Sparkles
} from 'lucide-react';
import HelpCenter from '@/components/HelpCenter';

const navItems = [
  { to: '/workspace', icon: Sparkles, label: 'Workspace' },
  { to: '/simple-dashboard', icon: Home, label: 'Simple View' },
  { to: '/', icon: LayoutDashboard, label: 'Advanced Dashboard' },
  { to: '/products', icon: Package, label: 'Products' },
  { to: '/suppliers', icon: Truck, label: 'Suppliers' },
  { to: '/invoices', icon: FileText, label: 'Invoices' },
  { to: '/pcf', icon: Calculator, label: 'PCF' },
  { to: '/unified-accounting', icon: BookOpen, label: 'Unified Accounting' },
  { to: '/living-pcf', icon: Activity, label: 'Living PCF' },
  { type: 'divider', label: 'Role Dashboards' },
  { to: '/cfo-dashboard', icon: DollarSign, label: 'CFO Dashboard' },
  { to: '/cpo-dashboard', icon: Users, label: 'CPO Dashboard' },
  { to: '/cso-dashboard', icon: Leaf, label: 'CSO Dashboard' },
  { type: 'divider', label: 'Integrations' },
  { to: '/settings/datev', icon: Link2, label: 'DATEV' },
  { to: '/settings/sap-integration', icon: Server, label: 'SAP' },
  { to: '/settings/import-data', icon: FileSpreadsheet, label: 'Import CSV' },
  { to: '/settings/mapping-profiles', icon: Bookmark, label: 'Mapping Profiles' },
  { to: '/settings/api-keys', icon: Key, label: 'API Keys' },
  { to: '/change-logs', icon: History, label: 'Change Logs' },
  { to: '/transparency', icon: Shield, label: 'Transparency' },
  { type: 'divider', label: 'Compliance' },
  { to: '/enterprise-reports', icon: FileText, label: 'Enterprise Reports' },
  { to: '/ai-automation', icon: Sparkles, label: 'AI Automation' },
  { to: '/security-activity', icon: Shield, label: 'Security Activity' },
  { to: '/mfa-setup', icon: KeyRound, label: 'MFA Setup' },
  { to: '/tuv-docs', icon: FileCheck2, label: 'TÜV Docs' },
  { to: '/shipments', icon: Ship, label: 'Shipments (ICS2)' },
  { to: '/ics2-generator', icon: FileCode2, label: 'ICS2 One-off' },
  { type: 'divider', label: 'Admin' },
  { to: '/admin/system-test', icon: ShieldCheck, label: 'System Test' },
  { to: '/admin/news', icon: PenSquare, label: 'Manage News' },
  { to: '/admin/rate-limits', icon: Gauge, label: 'Rate Limits' },
  { to: '/admin/email-logs', icon: Mail, label: 'Email Logs' },
  { type: 'divider', label: 'System' },
  { to: '/pricing', icon: Tag, label: 'Pricing' },
  { to: '/developers', icon: Code2, label: 'Developers' },
  { to: '/feedback', icon: MessageSquare, label: 'Feedback' },
  { to: '/contact', icon: Mail, label: 'Contact' },
  { to: '/news', icon: Newspaper, label: 'News' },
  { to: '/about', icon: Info, label: 'About' },
  { to: '/team', icon: Users2, label: 'Team' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onMobileClose}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        data-testid="app-sidebar"
        className={`fixed top-16 left-0 z-40 h-[calc(100vh-4rem)] border-r border-border bg-card/60 backdrop-blur-xl transition-all duration-300 flex flex-col
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${collapsed ? 'w-[68px]' : 'w-60'}
        `}
      >
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" data-testid="sidebar-nav">
          {navItems.map((item, idx) => {
            if (item.type === 'divider') {
              return (
                <div key={idx} className={`pt-4 pb-1 ${collapsed ? 'px-0' : 'px-3'}`}>
                  {!collapsed && (
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground/70">
                      {item.label}
                    </span>
                  )}
                  {collapsed && <div className="border-t border-border" />}
                </div>
              );
            }
            if (item.type === 'help-center') {
              return collapsed ? null : <div key="help-center" className="px-1"><HelpCenter /></div>;
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={onMobileClose}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `cp-sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`
                }
              >
                <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden lg:flex border-t border-border p-2">
          <button
            onClick={onToggle}
            data-testid="sidebar-collapse-btn"
            className="w-full flex items-center justify-center gap-2 rounded-lg py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <><ChevronsLeft className="w-4 h-4" /><span>Collapse</span></>}
          </button>
        </div>
      </aside>
    </>
  );
}
